/**
 * Branding Plugin
 * Loads and provides centralized branding configuration for multi-instance support
 */

let brandingConfig = null
let brandingLoaded = false
let loadingPromise = null

// Default fallback (only used if config fails to load)
const defaultBranding = {
  company: {
    name: 'Delivery App',
    shortName: 'App',
    tagline: 'Service de livraison',
    slogan: '',
    email: '',
    signatureLabel: 'Cachet et signature',
  },
  theme: {
    primary: '#114092',
    primaryDark: '#0d326f',
    primaryLight: '#1e5fd5',
    secondary: '#1D1D1B',
    secondaryLight: '#2d2d2b',
    accent: '#114092',
    accentAlt: '#1e5fd5',
    accentDark: '#0d326f',
  },
  logo: {
    main: '/images/logo/logo.png',
    svg: '/images/logo/logo.svg',
    alt: 'Company Logo',
  },
  meta: {
    title: 'Delivery Platform',
    description: 'Delivery management system',
  },
  features: {
    showSlogan: false,
  },
}

/**
 * Load branding configuration from JSON file
 */
async function loadBranding() {
  if (brandingLoaded) {
    return brandingConfig
  }

  if (loadingPromise) {
    return loadingPromise
  }

  loadingPromise = (async () => {
    try {
      const response = await fetch('/config/branding.json')
      if (!response.ok) {
        throw new Error(`Failed to load branding config: ${response.status}`)
      }
      brandingConfig = await response.json()
      brandingLoaded = true
      return brandingConfig
    } catch (error) {
      brandingConfig = defaultBranding
      brandingLoaded = true
      return brandingConfig
    }
  })()

  return loadingPromise
}

/**
 * Apply CSS variables to document root
 */
function applyCSSVariables(config) {
  const root = document.documentElement
  const { theme } = config

  // Apply theme colors as CSS variables
  root.style.setProperty('--brand-primary', theme.primary)
  root.style.setProperty('--brand-primary-dark', theme.primaryDark)
  root.style.setProperty('--brand-primary-light', theme.primaryLight)
  root.style.setProperty('--brand-secondary', theme.secondary)
  root.style.setProperty('--brand-secondary-light', theme.secondaryLight)
  root.style.setProperty('--brand-accent', theme.accent)
  root.style.setProperty('--brand-accent-alt', theme.accentAlt)
  root.style.setProperty('--brand-accent-dark', theme.accentDark)
  root.style.setProperty('--brand-gradient', `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`)
}

/**
 * Update document meta tags
 */
function updateMetaTags(config) {
  const { meta, company } = config

  // Update page title
  if (meta.title) {
    document.title = meta.title
  }

  // Update meta description
  const descriptionMeta = document.querySelector('meta[name="description"]')
  if (descriptionMeta && meta.description) {
    descriptionMeta.setAttribute('content', meta.description)
  }

  // Update Open Graph tags if they exist
  const ogTitle = document.querySelector('meta[property="og:title"]')
  if (ogTitle) {
    ogTitle.setAttribute('content', meta.title || company.name)
  }

  const ogDescription = document.querySelector('meta[property="og:description"]')
  if (ogDescription) {
    ogDescription.setAttribute('content', meta.description || company.tagline)
  }
}

/**
 * Get branding config synchronously (returns cached or null)
 */
function getBranding() {
  return brandingConfig || defaultBranding
}

/**
 * Vue Plugin Installation
 */
const BrandingPlugin = {
  /* eslint-disable no-param-reassign */
  install(Vue) {
    // Add global $branding property
    Vue.prototype.$branding = getBranding()

    // Add global mixin to ensure branding is available
    Vue.mixin({
      beforeCreate() {
        // Update $branding reference in case it was loaded after initial access
        if (brandingConfig) {
          this.$branding = brandingConfig
        }
      },
    })

    // Add global method to reload branding
    Vue.prototype.$reloadBranding = async function reloadBranding() {
      brandingLoaded = false
      loadingPromise = null
      await loadBranding()
      applyCSSVariables(brandingConfig)
      updateMetaTags(brandingConfig)
      this.$branding = brandingConfig
      return brandingConfig
    }
  },
  /* eslint-enable no-param-reassign */
}

/**
 * Initialize branding (call before Vue app creation)
 */
async function initBranding() {
  const config = await loadBranding()
  applyCSSVariables(config)
  updateMetaTags(config)
  return config
}

export {
  BrandingPlugin,
  initBranding,
  loadBranding,
  getBranding,
  defaultBranding,
}

export default BrandingPlugin
